import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, DataTable } from '@/components/kit';
import { useWallet } from '../lib/useManagerData';
import apiClient from '@/services/apiClient';
import { money } from '../lib/scope';

/**
 * Message wallet ▸ Ledger (f28) — opening, every movement, closing.
 *
 * A wallet screen that shows only a balance asks the clinic to trust a number
 * with nothing behind it. This is the month: what it opened with, what moved,
 * what it closed with, and for every debit the count and the rate it was
 * charged at, so the arithmetic can be checked rather than believed.
 *
 * WHAT THE ROWS ARE NAMED. The ledger records the CHANNEL a message went out
 * on, because that is what the debit knows when it is written; the board names
 * the PURPOSE ("Visit reminders", "Results released"), which nothing records
 * yet. So the rows read "WhatsApp messages · 96 × EGP 1.50" — true, and one
 * plumbing change away from the board's own words.
 */

interface Movement {
  kind: 'credit' | 'debit';
  entryType: string;
  resource: string | null;
  count: number;
  unitRateMinor: number | null;
  amountMinor: number;
  at: string | null;
  reference: string | null;
}

interface MonthLedger {
  period: string;
  openingMinor: number;
  closingMinor: number;
  movements: Movement[];
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

/** `2026-09` → "September". */
const monthName = (period: string) =>
  new Date(`${period}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' });

/* "1 Sep", not "1 Sept": en-GB's short month is four letters for September
   alone, and the boards write three everywhere. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayMonth = (d: Date) => `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;

/** The last day a month can carry, for the closing line's date. */
const lastDay = (period: string) => {
  const [y, m] = period.split('-').map(Number);
  return dayMonth(new Date(Date.UTC(y, m, 0)));
};

const firstDay = (period: string) => dayMonth(new Date(`${period}-01T00:00:00Z`));

const CREDIT_LABEL: Record<string, string> = {
  manual_topup: 'Top-up',
  reload: 'Auto top-up',
  grant: 'Credit from MAIE',
  refund: 'Undelivered · returned',
  reversal: 'Correction',
};

const CHANNEL_LABEL: Record<string, string> = {
  'message.whatsapp': 'WhatsApp messages',
  'message.sms': 'SMS messages',
  'message.telegram': 'Telegram messages',
  tokens: 'Assistant usage',
  'scribe.minutes': 'Scribe minutes',
};

function label(m: Movement): string {
  if (m.kind === 'credit') return CREDIT_LABEL[m.entryType] ?? 'Credit';
  return CHANNEL_LABEL[m.resource ?? ''] ?? 'Messages';
}

/** "96 × EGP 1.50", or the credit's own reference. */
function detail(m: Movement, currency: string): string {
  if (m.kind === 'credit') {
    const when = m.at
      ? dayMonth(new Date(m.at))
      : '';
    return [when, m.reference].filter(Boolean).join(' · ');
  }
  if (!m.unitRateMinor) return `${m.count.toLocaleString('en-US')} sent`;
  return `${m.count.toLocaleString('en-US')} × ${money(m.unitRateMinor / 100, currency)}`;
}

export function WalletLedger({
  period,
  currency,
  onRows,
}: {
  period: string;
  currency: string;
  /** The rows this screen is reading, so the bar's Export writes the same file. */
  onRows?: (rows: string[][]) => void;
}) {
  const go = useNavigate();
  const { wallet } = useWallet();
  const ar = wallet?.autoReload;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cm-wallet-ledger', period],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<MonthLedger | null> => {
      const res = await apiClient.get('/me/wallet/ledger', { params: { period } });
      return unwrap<MonthLedger | null>(res.data);
    },
  });

  /* Report the month as plain cells whenever it changes — the Export button
     lives on the top bar, which is the shell's, not this component's. */
  useEffect(() => {
    if (!data || !onRows) return;
    onRows([
      ['Opening balance', '', String(data.openingMinor / 100)],
      ...data.movements.map((m) => [
        label(m),
        detail(m, currency),
        String(m.amountMinor / 100),
      ]),
      ['Closing balance', '', String(data.closingMinor / 100)],
    ]);
  }, [data, currency, onRows]);

  if (isError) {
    return (
      <Card>
        <p className="t-sec mut">The month did not come back. Nothing has been changed.</p>
      </Card>
    );
  }

  const rows = [
    ...(data
      ? [{
          id: 'opening',
          cells: {
            movement: <span className="t-body">Opening balance · {firstDay(period)}</span>,
            rate: null,
            amount: <span className="t-strong num">{money(data.openingMinor / 100, currency)}</span>,
          },
        }]
      : []),
    ...(data?.movements ?? []).map((m, i) => ({
      id: `m${i}`,
      cells: {
        movement: <p className="t-strong">{label(m)}</p>,
        rate: <span className="t-sec mut">{detail(m, currency)}</span>,
        amount: (
          <span className={`w6 num ${m.amountMinor >= 0 ? 'grn' : ''}`}>
            {m.amountMinor >= 0 ? '+' : '−'}
            {money(Math.abs(m.amountMinor) / 100, currency).replace(`${currency} `, '')}
          </span>
        ),
      },
    })),
    ...(data
      ? [{
          id: 'closing',
          cells: {
            movement: <span className="t-strong">Closing balance · {lastDay(period)}</span>,
            rate: null,
            amount: <span className="t-title num">{money(data.closingMinor / 100, currency)}</span>,
          },
        }]
      : []),
  ];

  return (
    <>
      <DataTable
        caption={`${monthName(period)} — every movement of the message wallet`}
        columns={[
          { key: 'movement', label: 'Movement', width: 31 },
          { key: 'rate', label: 'Count × rate', width: 23 },
          { key: 'amount', label: 'Amount', width: 13 },
        ]}
        rows={rows}
        empty={
          <Card>
            <p className="t-sec mut">
              {isLoading
                ? 'Reading the month…'
                : `Nothing moved in ${monthName(period)} — no top-up, and no message sent.`}
            </p>
          </Card>
        }
      />
      <div className="cm-split" style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
        <Card style={{ flex: 1, minWidth: 220 }}>
          <p className="t-ovl mut">Auto top-up</p>
          <p className="t-sec">
            {ar?.enabled && ar.thresholdMinor != null && ar.reloadAmountMinor != null
              ? `below ${money(ar.thresholdMinor / 100, currency)} → add ${money(ar.reloadAmountMinor / 100, currency)}`
              : 'off — the wallet only moves when someone tops it up'}
          </p>
          <p className="t-cap mut">
            never more than {money(2000, currency)} at once or {money(10000, currency)} a month ·{' '}
            <button type="button" className="w6 brand"
              style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, font: 'inherit' }}
              onClick={() => go('/clinic-manager/settings/wallet?door=auto')}>
              open it
            </button>
          </p>
        </Card>
        <Card style={{ flex: 1, minWidth: 220 }}>
          <p className="t-ovl mut">Who reads this</p>
          <p className="t-sec">the accountant</p>
          <p className="t-cap mut">they book the top-up invoices as an expense and cannot change a line</p>
        </Card>
      </div>
      <p className="t-cap mut mt8">
        Every line is the ledger&rsquo;s own: a debit carries the count it was charged for and the
        rate frozen at the time, so the arithmetic can be checked rather than believed.
      </p>
    </>
  );
}
